import { TEMPLATE_PRODUCT_DETAIL, TEMPLATE_PRODUCT_LISTING } from '@/utils/cmsTemplateNames';
import type { NuxtApp } from '#app';

export default defineComponent({
  computed: {
    ...mapState(useMainStore, {
      devicePlatform: 'devicePlatform',
      pageTemplate: 'pageTemplate',
      language: 'language',
      defaultCountry: 'defaultCountry',
    }),

    ...mapState(useCartStore, {
      cart: 'cart',
    }),

    ...mapState(useUserStore, {
      trackerUtm: 'trackerUtm',
    }),

    platformToAppLink(): string {
      return this.devicePlatform ? this.adjustUrl : '';
    },

    adjustUrl(): string {
      return [
        this.$config.public.ADJUST_URL,
        this.joinQueryString({
          campaign: this.defaultCountry,
        }),
      ]
        .filter(this.filterHasValue)
        .join('?');
    },

    /**
     * App does not yet support the following PLP parameters:
     *  - sort: string
     *  - currentPage: number
     *
     * @see https://confluence.fressnapf.de/display/MA/Push+Notifications "Yep that's all we have" - App
     * @see https://help.adjust.com/en/article/deep-links All about adjust deep-links
     *
     * @returns {string}
     */
    deeplinkProductListingPage(): string {
      const productListDeepLink = 'fressnapfapp://product/list';
      const searchTerm = this.$route.query.text as string;
      const solrQuery = this.$route.query.q as string;
      const queryParams = {} as any;

      if (searchTerm) {
        queryParams.text = searchTerm;
      }

      if (solrQuery) {
        queryParams.q = solrQuery;
      }

      if (!(searchTerm || solrQuery)) {
        // Casting is necessary because we are in composition API context, the component instance of this mixin only
        // partially overlaps with the Nuxt type - but enough for our case, since we're only interested in `$pinia`
        queryParams.path = removeLanguagePrefix(this.$route.path, this as any as NuxtApp);
      }

      return [productListDeepLink, this.joinQueryString(queryParams, this.trackerUtm)]
        .filter(this.filterHasValue)
        .join('?');
    },

    deeplinkProductDetailPage() {
      const productDetailDeepLink = `fressnapfapp://product/details/${this.productId}`;

      return [productDetailDeepLink, this.trackerUtm].filter(this.filterHasValue).join('?');
    },

    deeplinkFriendsActivation() {
      const friendsActivationDeepLink = 'fressnapfapp://friends/activation';
      const { $host } = useNuxtApp();

      const queryParams = {
        redirectSuccess: `${$host}${this.$route.path}`,
        mergeCartGuid: this.cart?.guid ?? '',
      };

      return [friendsActivationDeepLink, this.joinQueryString(queryParams, this.trackerUtm)]
        .filter(this.filterHasValue)
        .join('?');
    },

    friendsActivationLink() {
      const delimiter = this.getParamsDelimiter(this.adjustUrl);

      const queryParams = {
        adgroup: encodeURIComponent('Teaser'),
        creative: encodeURIComponent('Switch to App to Activate Friends'),
        deeplink: encodeURIComponent(this.deeplinkFriendsActivation),
      };

      return [this.adjustUrl, this.joinQueryString(queryParams)].filter(this.filterHasValue).join(delimiter);
    },

    deeplinkDefault() {
      const defaultDeepLink = 'fressnapfapp://startpage';

      return [defaultDeepLink, this.trackerUtm].filter(this.filterHasValue).join('?');
    },

    productId() {
      const productUrl = (this.$route.params.product || this.$route.params.index) as string;
      const urlPattern = /(.+-)?(.*[^-])/;
      return productUrl?.match(urlPattern)?.pop();
    },
  },
  methods: {
    isPageTemplate(template: string) {
      return this.pageTemplate === template;
    },

    joinQueryString(data: any, queryStringExtra?: string | null): string {
      const queryString = Object.keys(data)
        .filter((key) => this.filterHasValue(data[key]))
        .map((key) => `${key}=${data[key]}`)
        .join('&');

      if (!queryStringExtra) {
        return queryString;
      }

      return [queryString, queryStringExtra].filter(this.filterHasValue).join('&');
    },

    getAppLink(): string {
      if (!this.platformToAppLink) {
        return '';
      }

      const delimiter = this.getParamsDelimiter(this.platformToAppLink);
      const fallbackUrl = this.$config.public[`ADJUST_FALLBACK_URL_${this.language.toUpperCase()}`] as string;

      let deeplink = this.deeplinkDefault as string;
      if (this.isPageTemplate(TEMPLATE_PRODUCT_LISTING)) {
        deeplink = this.deeplinkProductListingPage;
      } else if (this.isPageTemplate(TEMPLATE_PRODUCT_DETAIL)) {
        deeplink = this.deeplinkProductDetailPage;
      }

      return [
        this.platformToAppLink,
        this.joinQueryString({
          adgroup: encodeURIComponent('Smart Banner'),
          fallback: encodeURIComponent(fallbackUrl),
          deeplink: encodeURIComponent(deeplink),
        }),
      ]
        .filter(this.filterHasValue)
        .join(delimiter);
    },

    getParamsDelimiter(url: string) {
      return url.includes('?') ? '&' : '?';
    },

    filterHasValue(value: any) {
      return !!value;
    },
  },
});
